import React, { useState } from "react";
import styled from "styled-components/macro";
import {
  Avatar,
  Checkbox,
  Paper as MuiPaper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemText,
  Input,
  Tooltip,
  Box,
  Typography,
} from "@mui/material";
import {
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  Category as CategoryIcon,
} from "@material-ui/icons";
import RouterIcon from "@material-ui/icons/Router";
import { spacing } from "@material-ui/system";
import MaterialTable from "material-table";
import { getAllAPNodes, saveBlockCategories } from "../services/apServices";
import { getMediaAdsCategories } from "../services/mediaAdsServices";
import Swal from "sweetalert2";

const Paper = styled(MuiPaper)`
  ${spacing};

  border-radius: 22px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: 0 18px 42px rgba(15, 23, 42, 0.06);
  overflow: hidden;
`;

const SummaryStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 14px;
  padding: 20px 20px 0;
`;

const SummaryCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  border-radius: 18px;
  padding: 16px;
  background: ${(props) => props.background || "#f8fafc"};
  border: 1px solid rgba(148, 163, 184, 0.14);
`;

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export default function APCategoriasTable() {
  const [dataTable, setDataTable] = useState([]);
  const tableRef = React.createRef();
  const [categories, setCategories] = useState([]);
  const [blockedCategoriesMap, setBlockedCategoriesMap] = useState({});

  let getTableData = async () => {
    const apResponse = await getAllAPNodes();
    const apNodes = apResponse.data;

    const initialBlockedCategoriesMap = {};
    apNodes.forEach((node) => {
      initialBlockedCategoriesMap[node.accessPointInfo.id] =
        node.restrictedCategories || [];
    });

    setBlockedCategoriesMap(initialBlockedCategoriesMap);
    setDataTable(apNodes);

    const categoriesResponse = await getMediaAdsCategories();
    setCategories(categoriesResponse);
  };

  React.useEffect(() => {
    getTableData();
  }, []);

  const restrictedNodes = dataTable.filter(
    (node) => (blockedCategoriesMap[node.accessPointInfo.id] || []).length > 0
  ).length;
  const totalRestrictions = Object.values(blockedCategoriesMap).reduce(
    (total, current) => total + (current?.length || 0),
    0
  );

  const handleChange = (apID, event) => {
    setBlockedCategoriesMap((prev) => ({
      ...prev,
      [apID]: event.target.value, // Update the specific AP by its ID
    }));
  };

  return (
    <Paper>
      <SummaryStrip>
        <SummaryCard background="linear-gradient(135deg, rgba(14, 165, 233, 0.14), rgba(37, 99, 235, 0.16))">
          <Avatar style={{ background: "#1d4ed8" }}>
            <RouterIcon />
          </Avatar>
          <div>
            <Typography variant="caption" color="textSecondary">
              Access points
            </Typography>
            <Typography variant="h5">{dataTable.length}</Typography>
          </div>
        </SummaryCard>
        <SummaryCard>
          <Avatar style={{ background: "#0f766e" }}>
            <AssignmentTurnedInIcon />
          </Avatar>
          <div>
            <Typography variant="caption" color="textSecondary">
              Con restricciones
            </Typography>
            <Typography variant="h5">{restrictedNodes}</Typography>
          </div>
        </SummaryCard>
        <SummaryCard>
          <Avatar style={{ background: "#7c3aed" }}>
            <CategoryIcon />
          </Avatar>
          <div>
            <Typography variant="caption" color="textSecondary">
              Restricciones activas
            </Typography>
            <Typography variant="h5">{totalRestrictions}</Typography>
          </div>
        </SummaryCard>
      </SummaryStrip>
      <MaterialTable
        title=""
        tableRef={tableRef}
        data={dataTable}
        options={{
          grouping: false,
          actionsColumnIndex: -1,
          tableLayout: "auto",
          search: true,
          pageSize: 5,
          pageSizeOptions: [5, 10, 20],
          headerStyle: {
            backgroundColor: "#f8fbff",
            color: "#475569",
            fontSize: "0.72rem",
            fontWeight: 800,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
          },
          rowStyle: (rowData, index) => ({
            backgroundColor: index % 2 === 0 ? "#ffffff" : "#fbfdff",
          }),
          searchFieldStyle: {
            borderRadius: 999,
            backgroundColor: "#f8fafc",
            padding: "8px 14px",
          },
        }}
        localization={{
          body: {
            emptyDataSourceMessage: "No hay access points para configurar.",
          },
          header: {
            actions: "Acciones",
          },
          toolbar: {
            searchPlaceholder: "Buscar access point",
          },
        }}
        columns={[
          {
            title: "Nombre AP",
            field: "accessPointInfo.name",
            cellStyle: {
              textAlign: "left",
            },
            headerStyle: {
              textAlign: "left",
            },
          },
          {
            title: "Categoría AP",
            field: "accessPointInfo.category",
            cellStyle: {
              textAlign: "left",
            },
            headerStyle: {
              textAlign: "left",
            },
          },

          {
            title: "Restricciones de categorías de publicidades",
            field: "restrictedCategories",
            cellStyle: {
              textAlign: "left",
            },
            headerStyle: {
              textAlign: "left",
            },
            //Conditional Status
            render: (rowData) => (
              <FormControl
                sx={{ width: "100%", minWidth: 260, maxWidth: 560 }}
                size="small"
              >
                <InputLabel id={`select-label-${rowData.accessPointInfo.name}`}>
                  Categorias
                </InputLabel>
                <Select
                  labelId={`select-label-${rowData.accessPointInfo.name}`}
                  id={`select-${rowData.accessPointInfo.name}`}
                  multiple
                  value={blockedCategoriesMap[rowData.accessPointInfo.id] || []}
                  onChange={(event) =>
                    handleChange(rowData.accessPointInfo.id, event)
                  }
                  input={<Input />}
                  renderValue={(selected) => {
                    const names = selected
                      .map(
                        (id) => categories.find((cat) => cat.id === id)?.name
                      )
                      .filter(Boolean)
                      .join(", ");

                    return (
                      <Tooltip title={names} placement="top" arrow>
                        <Box
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {names}
                        </Box>
                      </Tooltip>
                    );
                  }}
                  MenuProps={MenuProps}
                  sx={{
                    borderRadius: "14px",
                    backgroundColor: "#ffffff",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      <Checkbox
                        checked={
                          blockedCategoriesMap[
                            rowData.accessPointInfo.id
                          ]?.includes(category.id) || false
                        }
                      />
                      <ListItemText primary={category.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ),
          },
        ]}
        actions={[
          (rowData) => ({
            icon: "save",
            tooltip: "save",
            onClick: (event, rowData) => {
              const apRestrictions = [
                {
                  accessPointId: rowData.accessPointInfo.id,
                  restrictions:
                    blockedCategoriesMap[rowData.accessPointInfo.id]?.map(
                      (id) => ({
                        id,
                      })
                    ) || [],
                },
              ];

              try {
                saveBlockCategories(apRestrictions);
                Swal.fire("Guardado!", "AP guardado exitosamente.", "success");
              } catch (error) {
                Swal.fire(
                  "Error!",
                  "Error guardando las restricciones.",
                  "error"
                );
              }
            },
          }),
        ]}
      />
    </Paper>
  );
}
